/**
 * @file SitioCard.js
 * @description Tarjeta de resumen de vehículos por sitio con indicadores de ocupación
 * @module components/cards/SitioCard
 * 
 * Características principales:
 * - Vista resumen de vehículos RFID en un sitio específico
 * - Barra de progreso visual de ocupación (verde/amarillo/rojo)
 * - Indicador de estado con punto de color en esquina superior derecha
 * - Desglose por marca (máximo 4 principales, ordenadas por cantidad)
 * - Logos de marcas cuando están disponibles
 * - Estados vacíos con diseño diferenciado
 * - Categorización por tipo de sitio (Agencia, Bodega, etc.)
 * - Clickable para navegar a vista de subsitios o detalle
 * 
 * Jerarquía de navegación:
 * Estados → Sitios (este componente) → Subsitios → Detalle
 * 
 * Niveles de ocupación (semáforo):
 * - Verde (0-50%): Ocupación baja, disponibilidad alta
 * - Amarillo (51-80%): Ocupación media, disponibilidad moderada
 * - Rojo (81-100%): Ocupación alta, disponibilidad limitada
 * - Gris: Sitio vacío
 * 
 * Tipos de sitio soportados:
 * - Agencia: Punto de venta principal
 * - Bodega: Almacenamiento temporal
 * - Taller: Servicios de mantenimiento
 * - Otros según categorización
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Building, MapPin, Car, Package, AlertCircle } from 'lucide-react';
import { getSitioCategoryColor } from '../../data/patiosData';

const BRANDS_PER_PAGE = 4;

/**
 * Mapeo de logos de marcas
 * URLs de imágenes de logos ubicadas en /public/logos/
 * Solo incluye marcas que tienen logo disponible
 * 
 * @const {Object} marcasLogos
 */
const marcasLogos = {
  'FORD': '/logos/ford.png',
  'LINCOLN': '/logos/lincoln.png',
  'GWM': '/logos/gwm.png',
  'MAZDA': '/logos/mazda.png',
};

/**
 * Componente SitioCard - Tarjeta de resumen por sitio
 * 
 * Muestra un resumen ejecutivo de los vehículos detectados por RFID en un
 * sitio específico. Incluye visualización de ocupación mediante:
 * - Barra de progreso con colores semáforo
 * - Punto indicador de estado en esquina
 * - Porcentaje de ocupación calculado
 * - Contador de vehículos vs capacidad
 * 
 * Lógica de capacidad:
 * - Si hay capacidad definida: muestra ocupación real y porcentaje
 * - Si no hay capacidad: barra al 100% si hay vehículos, 0% si está vacío
 * 
 * Desglose de marcas:
 * - Muestra máximo 4 marcas principales
 * - Ordenadas por cantidad descendente
 * - Con indicador si hay más marcas ocultas
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.sitio - Objeto con datos del sitio
 * @param {string} props.sitio.nombre - Nombre del sitio
 * @param {string} props.sitio.categoria - Categoría del sitio
 * @param {string} props.sitio.estado - Estado geográfico del sitio
 * @param {number} props.sitio.totalVehiculos - Total de vehículos en el sitio
 * @param {number} [props.sitio.capacidad=0] - Capacidad máxima del sitio
 * @param {number} [props.sitio.porcentajeOcupacion=0] - Porcentaje de ocupación (0-100)
 * @param {Object} props.sitio.marcas - Objeto {marca: cantidad} con desglose
 * @param {boolean} props.sitio.estaVacio - Si el sitio no tiene vehículos
 * @param {Function} props.onSitioClick - Callback al hacer click en la tarjeta
 * @returns {JSX.Element} Tarjeta del sitio
 */
const SitioCard = ({ sitio, onSitioClick }) => {
  /**
   * Determina si el sitio está vacío (sin vehículos)
   * Usado para cambiar el diseño y comportamiento visual
   */
  const estaVacio = sitio.totalVehiculos === 0;

  // Carousel de marcas
  const sortedMarcas = useMemo(() =>
    Object.entries(sitio.marcas || {}).sort((a, b) => b[1] - a[1]),
    [sitio.marcas]
  );
  const totalPages = Math.ceil(sortedMarcas.length / BRANDS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(0);
  const prevPage = useCallback((e) => { e.stopPropagation(); setCurrentPage(p => (p - 1 + totalPages) % totalPages); }, [totalPages]);
  const nextPage = useCallback((e) => { e.stopPropagation(); setCurrentPage(p => (p + 1) % totalPages); }, [totalPages]);

  /**
   * Porcentaje de ocupación del sitio (0-100)
   * Calculado como: (totalVehiculos / capacidad) * 100
   */
  const porcentajeOcupacion = sitio.porcentajeOcupacion || 0;

  /**
   * Obtiene clase de color para la barra de progreso
   * 
   * Sistema de semáforo:
   * - Gris: Sitio vacío (sin énfasis)
   * - Verde (0-50%): Disponibilidad alta, situación ideal
   * - Amarillo (51-80%): Disponibilidad moderada, atención preventiva
   * - Rojo (81-100%): Disponibilidad limitada, situación crítica
   * 
   * @returns {string} Clases Tailwind con gradiente de color
   */
  const getProgressBarColor = () => {
    if (estaVacio) return 'bg-gray-300';
    if (porcentajeOcupacion <= 50) return 'bg-gradient-to-r from-green-400 to-green-600';
    if (porcentajeOcupacion <= 80) return 'bg-gradient-to-r from-yellow-400 to-yellow-600';
    return 'bg-gradient-to-r from-red-400 to-red-600';
  };

  /**
   * Obtiene color del indicador de estado (punto en esquina)
   * 
   * Usa mismo sistema de semáforo que la barra de progreso.
   * El punto de color proporciona indicación rápida visual del estado.
   * 
   * @returns {string} Clase Tailwind de color de fondo
   */
  const getStatusIndicatorColor = () => {
    if (estaVacio) return 'bg-gray-400';
    if (porcentajeOcupacion <= 50) return 'bg-green-500';
    if (porcentajeOcupacion <= 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div
      className={`border rounded-lg p-3 sm:p-4 transition-all duration-200 cursor-pointer sm:hover:scale-[1.02] transform relative ${estaVacio
          ? 'bg-gray-50 border-gray-200 hover:shadow-md hover:bg-gray-100'
          : 'bg-white border-gray-300 hover:shadow-lg hover:bg-gray-50'
        } flex flex-col h-full`}
      onClick={() => onSitioClick && onSitioClick(sitio)}
    >

      {/* ===== INDICADOR DE ESTADO (punto de color) ===== */}
      {/* Posicionado en esquina superior derecha para referencia rápida */}
      <div className={`absolute top-3 right-3 w-3 h-3 rounded-full ${getStatusIndicatorColor()}`} />

      {/* ===== HEADER DEL SITIO ===== */}
      <div className="flex items-center justify-between mb-3 pr-4">
        {/* Nombre del sitio con ícono */}
        <div className="flex items-center space-x-2 min-w-0">
          <Building className={`w-4 h-4 shrink-0 ${estaVacio ? 'text-gray-500' : 'text-gray-600'}`} />
          <h3 className={`font-semibold text-sm sm:text-base truncate ${estaVacio ? 'text-gray-600' : 'text-gray-900'}`}>
            {sitio.nombre}
          </h3>
        </div>

        {/* Badge de categoría del sitio */}
        <span className={`text-xs px-2 py-0.5 sm:py-1 rounded-full shrink-0 ml-1 ${getSitioCategoryColor(sitio.categoria)}`}>
          {sitio.categoria}
        </span>
      </div>

      {/* ===== UBICACIÓN DEL SITIO ===== */}
      <div className="flex items-center space-x-1 mb-3 text-xs text-gray-500">
        <MapPin className="w-3 h-3" />
        <span>{sitio.estado}</span>
      </div>

      {/* ===== INFORMACIÓN DE CAPACIDAD Y OCUPACIÓN ===== */}
      <div className="mb-3">
        {/* Header: Título y contador */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-1">
            <Car className={`w-4 h-4 ${estaVacio ? 'text-gray-500' : 'text-gray-600'}`} />
            <span className={`text-sm ${estaVacio ? 'text-gray-500' : 'text-gray-600'}`}>
              {estaVacio ? 'Sitio vacío' : 'Vehículos'}
            </span>
          </div>

          {/* Contador: actual / capacidad */}
          <div className="flex items-center space-x-2">
            <span className={`text-sm font-bold ${estaVacio ? 'text-gray-500' : 'text-gray-900'}`}>
              {sitio.totalVehiculos}
            </span>
            {sitio.capacidad > 0 && (
              <span className="text-xs text-gray-400">
                / {sitio.capacidad}
              </span>
            )}
          </div>
        </div>

        {/* Barra de progreso visual */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${getProgressBarColor()}`}
            style={{
              // Si hay capacidad: usar porcentaje real (max 100%)
              // Si no hay capacidad: 100% si tiene vehículos, 0% si está vacío
              width: sitio.capacidad > 0
                ? `${Math.min(porcentajeOcupacion, 100)}%`
                : estaVacio ? '0%' : '100%'
            }}
          />
        </div>

        {/* Indicador de porcentaje (solo si hay capacidad definida) */}
        {sitio.capacidad > 0 && (
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-gray-400">Ocupación</span>
            <span className={`text-xs font-medium ${estaVacio ? 'text-gray-400' :
                porcentajeOcupacion <= 50 ? 'text-green-600' :
                  porcentajeOcupacion <= 80 ? 'text-yellow-600' : 'text-red-600'
              }`}>
              {porcentajeOcupacion.toFixed(0)}%
            </span>
          </div>
        )}
      </div>

      {/* ===== CONTENIDO PRINCIPAL: MARCAS O ESTADO VACÍO ===== */}
      {estaVacio ? (
        /* SITIO VACÍO: Diseño especial sin datos */
        <div className="space-y-3 mb-3">
          <div className="flex items-center justify-center py-4 border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center">
              <Package className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 font-medium">Sin vehículos</p>
              <p className="text-xs text-gray-400">Sitio disponible</p>
            </div>
          </div>
        </div>
      ) : (
        /* SITIO CON VEHÍCULOS: Desglose por marcas con carousel */
        <div>
          {/* Carousel */}
          <div className="overflow-hidden">
            <div
              style={{
                display: 'flex',
                transform: `translateX(-${currentPage * 100}%)`,
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {Array.from({ length: totalPages }).map((_, pageIdx) => {
                const pageBrands = sortedMarcas.slice(
                  pageIdx * BRANDS_PER_PAGE,
                  (pageIdx + 1) * BRANDS_PER_PAGE
                );
                return (
                  <div key={pageIdx} style={{ minWidth: '100%' }} className="space-y-2">
                    {pageBrands.map(([marca, cantidad]) => (
                      <div key={marca} className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-2">
                          {marcasLogos[marca] && (
                            <img
                              src={marcasLogos[marca]}
                              alt={`${marca} logo`}
                              className="w-[18px] h-[18px] object-contain"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          <span className="text-gray-700 text-xs">{marca}</span>
                        </div>
                        <span className="font-medium text-gray-900 text-xs">{cantidad}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Paginación — solo si hay más de una página */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-1.5" onClick={e => e.stopPropagation()}>
              <button onClick={prevPage} aria-label="Página anterior"
                className="w-5 h-5 flex items-center justify-center rounded text-[#1d2e61] hover:bg-[#1d2e61]/10 transition-all">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <span className="text-[10px] font-medium text-gray-500 tabular-nums">
                {currentPage + 1}/{totalPages}
              </span>
              <button onClick={nextPage} aria-label="Página siguiente"
                className="w-5 h-5 flex items-center justify-center rounded text-[#1d2e61] hover:bg-[#1d2e61]/10 transition-all">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== FOOTER CON INDICADOR DE ACCIÓN ===== */}
      <div className="mt-auto pt-3 border-t border-gray-100">
        <p className={`text-xs text-center flex items-center justify-center space-x-1 ${estaVacio ? 'text-gray-400' : 'text-gray-500'
          }`}>
          {estaVacio ? (
            /* Mensaje para sitio vacío */
            <>
              <span>Sitio disponible</span>
              <AlertCircle className="w-3 h-3" />
            </>
          ) : (
            /* Mensaje para sitio con vehículos */
            <>
              <span>Ver vehículos</span>
              {/* Flecha derecha */}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default SitioCard;